/**
 * Rich test fixture for JobOfferFormModal initialValues.
 * Matches the frontend jobOffer model shape (nested, camelCase enum values).
 * Usage:
 *   import testJobOfferValues from "@/mocks/testJobOfferValues";
 *   <JobOfferFormModal initialValues={testJobOfferValues} ... />
 */
const testJobOfferValues = {
    id: null,

    basicInfo: {
        positionName: "Senior Frontend Developer",
        department: "Dział Technologii",
        companyName: "TechCorp Sp. z o.o.",
        location: "Warszawa",
        workMode: "hybrid",
        vacancies: 2,
        plannedStartDate: "2026-08-01",
        seniorityLevel: "senior",
        offerStatus: "draft",
    },

    positionDescription: {
        shortDescription:
            "Szukamy doświadczonego Senior Frontend Developera do zespołu produktowego budującego platformę SaaS dla sektora HR. Pracujesz w nowoczesnym stacku technologicznym i masz realny wpływ na architekturę.",
        mainResponsibilities: [
            "Projektowanie i implementacja komponentów UI w React i TypeScript",
            "Code review i mentoring junior developerów",
            "Współpraca z designerami i Product Ownerami przy definiowaniu rozwiązań UX",
            "Optymalizacja wydajności aplikacji i monitorowanie Core Web Vitals",
        ],
        responsibilitiesScope: [
            "Utrzymanie i rozwijanie frontendu platformy SaaS",
            "Definiowanie standardów kodowania i dobrych praktyk w zespole",
            "Integracja z REST API i WebSocket",
            "Udział w planowaniu sprintów i retrospektywach",
        ],
        positionGoals:
            "W pierwszym kwartale wdrożenie nowego modułu raportowania. W ciągu roku — migracja legacy kodu do React 19 i zmniejszenie bundle size o 30%.",
        typicalWorkday:
            "Stand-up o 9:30, praca nad taskami do 13:00, lunch, code review i pair programming po południu. Dwa razy w tygodniu sesje z designerami.",
        teamStructure:
            "Zespół 8 osób: 2 seniorów, 3 midów, 2 juniorów, 1 tech lead. Pracujemy metodą Scrum z dwutygodniowymi sprintami.",
        supervisor: "Marta Kowalska, Head of Engineering",
        toolsAndSystems: ["React 18+", "TypeScript", "Vite", "Chakra UI", "React Query", "Jest", "Playwright", "GitHub Actions", "Figma", "Jira"],
        benefits: [
            "Prywatna opieka medyczna (Luxmed)",
            "Karta Multisport",
            "Budżet szkoleniowy 5000 PLN/rok",
            "Dofinansowanie do sprzętu (MacBook Pro)",
            "Elastyczne godziny pracy",
            "Stock options po 12 miesiącach",
        ],
        recruitmentStages: [
            "Screening HR (30 min)",
            "Rozmowa techniczna z Tech Leadem (1h)",
            "Task techniczny (do 4h, płatny)",
            "Rozmowa z managerem i omówienie tasku (1h)",
            "Oferta",
        ],
    },

    employmentForms: [
        {
            type: "employmentContract",
            preferredType: "",
            isEliminatingIfNotAccepted: false,
            weight: 5,
            aiQuestion: "Czy akceptujesz zatrudnienie na umowie o pracę?",
        },
        {
            type: "b2b",
            preferredType: "",
            isEliminatingIfNotAccepted: false,
            weight: 5,
            aiQuestion: "Czy prowadzisz działalność gospodarczą lub jesteś w stanie założyć B2B?",
        },
    ],

    salaryModels: [
        {
            type: "fixedMonthly",
            currency: "PLN",
            hasBonus: true,
            canCommunicateToCandidate: true,
            isNegotiable: true,
            hourly: null,
            fixedSalary: {
                grossMonthlyFrom: 18000,
                grossMonthlyTo: 25000,
                isFixedDescription: "",
            },
            pieceRate: null,
            b2b: null,
        },
        {
            type: "projectBased",
            currency: "PLN",
            hasBonus: false,
            canCommunicateToCandidate: true,
            isNegotiable: true,
            hourly: null,
            fixedSalary: null,
            pieceRate: null,
            b2b: {
                netInvoiceAmountFrom: 22000,
                netInvoiceAmountTo: 30000,
                settlementPeriod: "monthly",
                paymentTerm: "14 dni od wystawienia faktury",
            },
        },
    ],

    bonuses: [
        {
            name: "Bonus kwartalny",
            type: "quarterly",
            isGuaranteed: false,
            calculationDescription: "Do 15% wynagrodzenia podstawowego, zależny od wyników sprintu i oceny managera",
            amountFrom: 2000,
            amountTo: 4000,
            canCommunicateToCandidate: true,
        },
    ],

    allowances: [
        {
            name: "Dofinansowanie pracy zdalnej",
            type: "remoteWorkAllowance",
            calculationDescription: "Stały ryczałt miesięczny na pokrycie kosztów internetu i miejsca pracy",
            amountFrom: 300,
            amountTo: 300,
            canCommunicateToCandidate: true,
        },
    ],

    workingTime: {
        workloadType: "fullTime",
        minHoursPerWeek: null,
        maxHoursPerWeek: null,
        workSystem: "oneShift",
        startTime: "09:00",
        endTime: "17:00",
        isFixedSchedule: false,
        isFlexibleSchedule: true,
        worksWeekends: false,
        worksHolidays: false,
        hasBusinessTrips: false,
        worksNightShifts: false,
        hasOnCallDuty: false,
        overtime: {
            exists: true,
            frequency: "occasional",
            isMandatory: false,
            settlementMethod: "timeOff",
            refusalIsEliminating: false,
        },
    },

    workConditions: {
        physicalWork: false,
        workingAtHeight: false,
        workingWithMachinery: false,
        nightWork: false,
        outdoorWork: false,
        drivingVehicles: false,
        chemicalSubstancesContact: false,
        noiseExposure: false,
        dustExposure: false,
        customerContact: true,
        other: "",
    },

    riskFactors: [],

    protectiveEquipment: [],

    requiredDocuments: [
        {
            name: "CV",
            documentType: "cv",
            status: "required",
            criticality: "critical",
            requiresUpload: true,
            mustBeCurrent: true,
            legacyScoring: {
                isRequired: true,
                isCritical: true,
                isEliminating: true,
                weight: 0,
                minAcceptedValue: null,
                preferredValue: null,
                askCandidate: false,
                questionText: "",
                followUpQuestionText: "",
                internalComment: "Weryfikujemy projekty i historię zatrudnienia",
            },
        },
        {
            name: "Portfolio / przykłady kodu",
            documentType: "portfolio",
            status: "optional",
            criticality: "important",
            requiresUpload: false,
            mustBeCurrent: false,
            legacyScoring: {
                isRequired: false,
                isCritical: false,
                isEliminating: false,
                weight: 10,
                minAcceptedValue: null,
                preferredValue: "Link do GitHuba lub demo aplikacji",
                askCandidate: true,
                questionText: "Czy możesz podać link do swojego portfolio lub repozytorium z przykładami kodu?",
                followUpQuestionText: "Który projekt jest Twoim największym wyzwaniem technicznym i dlaczego?",
                internalComment: "",
            },
        },
    ],

    skills: [
        {
            name: "React",
            category: "technical",
            status: "required",
            criticality: "critical",
            ratingScale: "scale1to5",
            aiAsksLevel: true,
            requiresConfirmation: false,
            confirmationType: "none",
            legacyScoring: {
                isRequired: true,
                isCritical: true,
                isEliminating: true,
                weight: 25,
                minAcceptedValue: "4",
                preferredValue: "5",
                askCandidate: true,
                questionText: "W skali 1-5 jak oceniasz swoje umiejętności w React?",
                followUpQuestionText: "Opisz przypadek użycia React Suspense lub Concurrent Mode w Twoim projekcie.",
                internalComment: "Minimum 4 lata komercyjnego doświadczenia z React",
            },
        },
        {
            name: "TypeScript",
            category: "technical",
            status: "required",
            criticality: "critical",
            ratingScale: "scale1to5",
            aiAsksLevel: true,
            requiresConfirmation: false,
            confirmationType: "none",
            legacyScoring: {
                isRequired: true,
                isCritical: true,
                isEliminating: true,
                weight: 20,
                minAcceptedValue: "3",
                preferredValue: "5",
                askCandidate: true,
                questionText: "W skali 1-5 jak oceniasz swoje umiejętności w TypeScript?",
                followUpQuestionText: "Jak używasz typów generycznych i conditional types w praktyce?",
                internalComment: "",
            },
        },
        {
            name: "Testy jednostkowe i e2e (Jest, Playwright)",
            category: "technical",
            status: "required",
            criticality: "important",
            ratingScale: "scale1to5",
            aiAsksLevel: true,
            requiresConfirmation: false,
            confirmationType: "none",
            legacyScoring: {
                isRequired: true,
                isCritical: false,
                isEliminating: false,
                weight: 15,
                minAcceptedValue: "3",
                preferredValue: "4",
                askCandidate: true,
                questionText: "W skali 1-5 jak oceniasz swoje doświadczenie z testowaniem aplikacji frontend?",
                followUpQuestionText: "",
                internalComment: "",
            },
        },
        {
            name: "Angielski",
            category: "language",
            status: "required",
            criticality: "critical",
            ratingScale: "levelWords",
            aiAsksLevel: true,
            requiresConfirmation: false,
            confirmationType: "none",
            legacyScoring: {
                isRequired: true,
                isCritical: true,
                isEliminating: true,
                weight: 10,
                minAcceptedValue: "B2",
                preferredValue: "C1",
                askCandidate: true,
                questionText: "Jak oceniasz swój poziom angielskiego w komunikacji technicznej?",
                followUpQuestionText: "",
                internalComment: "Dokumentacja i code review w języku angielskim",
            },
        },
    ],

    experienceRequirements: [
        {
            area: "Komercyjne projekty React",
            minYears: 4,
            status: "required",
            legacyScoring: {
                isRequired: true,
                isCritical: true,
                isEliminating: true,
                weight: 20,
                minAcceptedValue: "4",
                preferredValue: "6",
                askCandidate: true,
                questionText: "Ile lat komercyjnego doświadczenia posiadasz w projektach React?",
                followUpQuestionText: "Opisz największy projekt React, w którym uczestniczyłeś — skala, zespół, wyzwania.",
                internalComment: "",
            },
        },
        {
            area: "Praca w Agile / Scrum",
            minYears: 2,
            status: "optional",
            legacyScoring: {
                isRequired: false,
                isCritical: false,
                isEliminating: false,
                weight: 5,
                minAcceptedValue: null,
                preferredValue: null,
                askCandidate: true,
                questionText: "Czy pracowałeś wcześniej w metodologii Agile lub Scrum?",
                followUpQuestionText: "",
                internalComment: "",
            },
        },
    ],

    customQuestions: [
        {
            questionText: "Skąd dowiedziałeś się o tej ofercie?",
            answerType: "singleChoice",
            isRequired: false,
            isEliminating: false,
            expectedAnswer: "",
            eliminatingAnswer: "",
            weight: 0,
            aiCanFollowUp: false,
            followUpQuestionText: "",
        },
        {
            questionText: "Czy jesteś gotowy podjąć pracę w ciągu 1 miesiąca?",
            answerType: "yesNo",
            isRequired: true,
            isEliminating: false,
            expectedAnswer: "Tak",
            eliminatingAnswer: "",
            weight: 5,
            aiCanFollowUp: true,
            followUpQuestionText: "Jaki jest Twój aktualny okres wypowiedzenia?",
        },
        {
            questionText: "Jakie jest Twoje doświadczenie z architekturą mikrofrontendów?",
            answerType: "text",
            isRequired: false,
            isEliminating: false,
            expectedAnswer: "",
            eliminatingAnswer: "",
            weight: 8,
            aiCanFollowUp: true,
            followUpQuestionText: "Jakiego narzędzia (Module Federation, Single-SPA, Bit) używałeś i dlaczego?",
        },
    ],

    motivationQuestions: [
        {
            questionText: "Co Cię motywuje do zmiany pracy?",
            expectedAnswer: "Chęć rozwoju technicznego, nowe wyzwania, ciekawszy produkt",
            positiveSignals: "Mówi o chęci nauki, wyzwaniach technicznych, pracy nad produktem z realnym impaktem",
            riskSignals: "Wyłącznie motywacja finansowa, narzekanie na poprzednią firmę, brak konkretów",
            weight: 10,
            canAnswerEliminate: false,
        },
        {
            questionText: "Gdzie widzisz siebie za 2-3 lata?",
            expectedAnswer: "Rola Tech Leada lub architekta, rozwijanie kompetencji miękkich i przywódczych",
            positiveSignals: "Ambicje kierownicze lub specjalizacja, plan rozwoju, chęć mentorowania",
            riskSignals: "Brak planów, niestabilność, plany emigracji lub branżowej zmiany",
            weight: 5,
            canAnswerEliminate: false,
        },
    ],

    financialExpectations: {
        aiAsksAboutExpectations: true,
        acceptableRangeFrom: 16000,
        acceptableRangeTo: 28000,
        preferredRangeFrom: 18000,
        preferredRangeTo: 24000,
        eliminatingAbove: 32000,
        currency: "PLN",
        exceedingBudgetIsEliminating: true,
        weight: 10,
    },

    availability: {
        earliestAcceptedStartDate: "2026-07-01",
        maxNoticePeriodDays: 90,
        aiAsksAboutAvailability: true,
        lackOfQuickAvailabilityIsEliminating: false,
        weight: 5,
        requiresShiftWorkReadiness: false,
        requiresWeekendReadiness: false,
        requiresBusinessTripReadiness: false,
        requiresOvertimeReadiness: true,
    },

    disqualificationRules: [
        {
            name: "Brak doświadczenia z React",
            conditionDescription: "Kandydat nie posiada żadnego komercyjnego doświadczenia z React lub deklaruje mniej niż 2 lata",
            relatedFormField: "skills.React",
            eliminatingValue: "0",
            isAutomaticElimination: true,
            requiresManualDecision: false,
            recruiterComment: "Absolutne minimum dla tej roli",
        },
        {
            name: "Oczekiwania finansowe powyżej budżetu",
            conditionDescription: "Kandydat oczekuje powyżej 32000 PLN brutto",
            relatedFormField: "financialExpectations",
            eliminatingValue: "32000",
            isAutomaticElimination: false,
            requiresManualDecision: true,
            recruiterComment: "Do decyzji managera",
        },
    ],

    aiConversationConfig: {
        conversationStyle: "professional",
        language: "pl",
        canAnswerAboutSalary: true,
        canAnswerAboutBenefits: true,
        canAnswerAboutCompany: true,
        confidentialInfo: "Nie ujawniaj że klient to TechCorp — projekt jest pod NDA. Firma to 'nasz klient z sektora HR'.",
        fallbackAnswer: "To jest świetne pytanie — odpowiedź na nie chętnie omówimy na rozmowie z rekruterem.",
        maxFollowUpQuestions: 2,
    },

    scoringConfig: {
        maxTotalPoints: 100,
        veryGoodMatchThreshold: 85,
        goodMatchThreshold: 65,
        partialMatchThreshold: 40,
        eliminationBlocksRanking: true,
        categoryMaxPoints: {
            documents: 5,
            criticalSkills: 30,
            optionalSkills: 10,
            experience: 20,
            availability: 5,
            financialExpectations: 10,
            motivation: 10,
            safety: 0,
            overtimeAndShifts: 0,
            customQuestions: 10,
        },
    },

    hiddenSections: [],
};

export default testJobOfferValues;
